import _ from 'lodash'
import $ from 'jquery'
import Backbone from 'backbone'
import template from './data-source-form.hbs'
import i18n from 'scoreboard/components/i18n'
import Dataset, {isEditField} from './model/dataset'
const { isTotal } = Dataset
import selects from 'scoreboard/components/selects'
import dropdown from 'scoreboard/components/dropdown'
import * as aggregationTypeOptions from './aggregation-type-options'
import DatasetsFilterBadges from 'scoreboard/datasets/filters/datasets-filter-badges'
import formHelper from 'scoreboard/components/form-helper'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import aggregationLabel from './overview/aggregation-label'
import renderFieldDropdown from 'scoreboard/datasets/render-field-dropdown'
import totalOption from './total-option'
import unwrapModel from 'scoreboard/components/unwrap-model'
import wrapModel from 'scoreboard/components/wrap-model'
import BasePercentagesOnPanel from './base-percentages-on-panel'
import EditableButton from 'scoreboard/components/editable-button'
import DataSourceFieldEquationForm from './data-source-field-equation-form'
import appDisplay from 'scoreboard/components/app-display'
import DatasetsEquationForm from 'scoreboard/datasets/equations/datasets-equation-form'
import PlaceholderSelect from 'scoreboard/components/placeholder-select'
import ObjectSelectorButton from 'scoreboard/components/object-selector-button'
import * as datasetSelectorOptions from 'scoreboard/datasets/dataset-selector-options'
import DataTypeSelector from 'scoreboard/datasets/edit/fields/data-type-selector'
import DataSourceLink from 'scoreboard/components/data-source-selector/data-source-link'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import isGeographicOrRollup from './is-geographic-or-rollup'
import DataType from 'scoreboard/datasets/model/data-type'
import { LIST_UNIQUE_VALUES } from './model/aggregation-type'
const { DATE, TIME, DATE_TIME, ROLLUP, DATASET_LINK } = DataType

var DatasetDataSourceForm = Backbone.View.extend({

	/**
	 * Form used to add or edit a dataset data source. For example, a dataset
	 * table column or a dataset report designer column
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} [options.model] the starting Dataset
	 *
	 * @param {Object} [options.table] the dataset table the column belongs to,
	 * if applicable. Used when working with percentage aggregations and to
	 * determine if it's appropriate to show the aggregation type dropdown
	 *
	 * @param {Object} options.dataSource the data source being edited
	 *
	 * @param {Object} options.treeItemsById a hash of tree items keyed by ID
	 *
	 * @param {String} [options.allowAppCalendar] whether "Current Calendar"
	 * should be an option when working with date filters
	 *
	 * @param {Dataset[]} options.datasets all the top-level datasets the
	 * user has permission to view
	 *
	 * @param {Boolean} [options.allLinkedFields=false] whether to show all
	 * linked fields rather than just the explicitly defined visible linked
	 * fields when building filters
	 *
	 * @param {Boolean} [options.hideAggregationTypeForSameDataset=false] whether
	 * to hide the aggregation type dropdown when modifying a reference to the
	 * same dataset as the main dataset
	 *
	 * @param {FormView} options.formView - the FormView that manages this view
	 * (and potentially other views)
	 *
	 * @param {Boolean} [options.numericSumOnly=false] indicates that the field
	 * must be numeric, and only the sum aggregation type is allowed
	 *
	 * @param {Boolean} [options.showAggregationType] whether the
	 * aggregation type dropdown should be displayed
	 *
	 * @param {Boolean} [options.allowCalculated=true] whether calculated data
	 * sources are allowed
	 *
	 * @param {String} [options.parentDomainType] the DomainType of the parent
	 * object this data source is associated with. For example, if this is for
	 * a chart serie, parentDomainType would be 'chart'.
	 *
	 * @param {Boolean} [options.useStartingDataset=false] whether or not
	 * basic data sources should have an option for selecting a dataset. See
	 * https://github.com/SpiderStrategies/Scoreboard/issues/45540#issuecomment-1050099534
	 *
	 * @param {String} [options.defaultDateAggregationType] when selecting a date
	 * field, this is the aggregation type to default to
	 *
	 * @param {Boolean} [options.includeTotal=true] indicates that "Total X"
	 * options may be shown in the select if they were previously added as visible
	 * linked fields.
	 *
	 * @param {Boolean} [options.includeNativeTotal=true] whether
	 * to add a "Number of X" option for the native root dataset
	 *
	 * @param {Function} [options.restrictTemporalToNumUnique=false] indicates that
	 * temporal data types (date, time, and date/time) should be restricted to
	 * the number of unique aggregation type. This is useful for spots like
	 * gauges which can't reasonable show dates or times.
	 *
	 * @param {String} [options.warningBannerMessage] a message to display in
	 * a warning banner if needed (already i18n'd)
	 *
	 * @param {Boolean} [options.includeEditFields=true] whether to include the
	 * edit fields in the data source form field dropdown
	 *
	 * @param {Boolean} [options.allowListUniqueValues=true] indicates that the
	 * "List Unique Values" aggregation type should be allowed
	 */
	initialize: function(options = {}) {
		this.options = options
		this.filters = _.get(options.dataSource, 'filters', [])
		if (_.isString(this.filters)) {
			// filters in value widgets are stored as Strings
			this.filters = JSON.parse(this.filters)
		}
		this.filters = _.cloneDeep(this.filters)
		this.equation = options.dataSource.equation
		this.aggregations = options.dataSource.aggregations
		this.allLinkedFields = _.get(options, 'allLinkedFields',
			false)
		this.numericSumOnly = _.get(options, 'numericSumOnly', false)
		this.treeItemsById = _.cloneDeep(_.get(options, 'treeItemsById', {}))
		this.hideAggregationTypeForSameDataset = _.get(options,
			'hideAggregationTypeForSameDataset', false)
		this.showAggregationType = _.isNil(options.showAggregationType) ?
			this.showAggregationType : options.showAggregationType
		this.allowCalculated = _.get(options, 'allowCalculated', true)
		this.useStartingDataset = _.get(options, 'useStartingDataset', false)
		this.isChartDataSource = options.parentDomainType === 'chart'
		this.includeTotal = this.options.includeTotal ?? true
		this.includeNativeTotal = this.options.includeNativeTotal ?? true
		this.includeEditFields = _.get(options, 'includeEditFields', true)
		this.allowListUniqueValues = options.allowListUniqueValues ?? true

		this.formView = options.formView
	},

	/**
	 * The starting dataset. When the user changes the dataset dropdown,
	 * this.model is updated to the dataset referred to in the dropdown.
	 * May be null if there is no starting dataset.
	 * @type {Dataset}
	 */
	model: null,

	className: 'data-source-form',

	/**
	 * Whether to display the "percentage" option in the "aggregation type"
	 * dropdown
	 */
	allowPercentageAggregationType: true,
	/**
	 * Whether the label field should be displayed
	 */
	showLabel: false,
	/**
	 * Whether the dataset dropdown should be displayed
	 */
	showDataset: true,
	/**
	 * Whether the aggregation type dropdown should be displayed
	 */
	showAggregationType: true,
	/**
	 * Whether the decimal precision dropdown should be displayed
	 */
	showDecimalPrecision: true,
	invalidDataset: false,

	events: {
		'change [name=fieldIdentifier]': 'onChangeFieldIdentifier',
		'click .set-equation': 'onClickSetEquation',
		'change [name=dataType]': 'renderAggregationType',
		'change [name=aggregationType]': 'onChangeAggregationType',
		'change [name=labelType]': 'changeLabelType',
		'click .filters.panel .add-filter': 'addFilter',
		'click .starting-dataset .clear': 'onClickStartingDatasetClear'
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		this.renderDecimalPrecision()

		this.setupDatasetSelectPlaceholder()

		// #28303 must be before writeToDom
		this.renderDataTypeSelector()
		this.renderDataSourceTypeDropdown()

		formHelper.writeToDom(this.options.dataSource, this.el)

		this.renderStartingDatasetButton()
		this.renderFieldDropdown()
		this.renderEquationButton()
		this.renderFilterBadges()
		// must be after this.renderFilterBadges so that the allLinkedFields
		// property is set correctly
		this.onChangeDataSourceType(this.dataSourceTypeDropdown.val()) // this calls this.renderAggregationType()
		this.$('.label-container, .custom-name').toggleClass(
			'is-not-displayed', !this.showLabel)
		this.updateLabelTypeText()
		this.renderLabelTypeAndLabel()
		this.changeLabelType()
		this.renderBasePercentagesOnPanel()
		this.onChangeAggregationType()
		// needs to be after formHelper
		this.toggleAggregationType()
		this.renderDataSourceLink()
		this.renderWarningBanner()

		return this
	},

	renderDecimalPrecision: function() {
		if (this.showDecimalPrecision) {
			dropdown.precision(this.$('[name=precision]'))
		} else {
			this.$('.precision').remove()
		}
	},

	setupDatasetSelectPlaceholder: function() {
		this.placeholderSelect = new PlaceholderSelect()
		this.$('.placeholder-select').replaceWith(this.placeholderSelect.el)
		this.placeholderSelect.render()
	},

	renderBasePercentagesOnPanel: function() {
		this.basePercentagesOnPanel = new BasePercentagesOnPanel({
			model: this.model,
			formView: this.formView,
			aggregation: this.options.dataSource,
			table: this.options.table,
			treeItemsById: this.treeItemsById,
			numeratorFiltersRetriever: () => this.filters
		})
		this.$el.append(this.basePercentagesOnPanel.el)
		this.basePercentagesOnPanel.render()

		if (!this.allowPercentageAggregationType ||
			!_.result(this, 'showAggregationType')) {
			this.$('.base-percentages-on').addClass('-is-not-displayed')
		}

		let updateColumnFiltersTitle = checked => {
			let key = checked ?
				'datasets.choose.columns.columnFilters' :
				'datasets.choose.columns.columnFilters.clarified'
			this.$('.filters.panel h3').text(i18n.get(key))
		}
		this.basePercentagesOnPanel.toggle.on('change', updateColumnFiltersTitle)
		updateColumnFiltersTitle(this.basePercentagesOnPanel.toggle.val())
	},

	context: function() {
		let title = i18n.get('datasets.choose.columns.setDataSource')
		return { title }
	},

	renderStartingDatasetButton: function() {
		if (!this.showDataset) {
			this.$('.starting-dataset').addClass('is-not-displayed')
		}

		_.invoke(this.datasetSelector, 'remove')

		let startingDatasetId = this.getOriginalStartingDatasetId()
		let startingDataset = startingDatasetId
			? _.find(this.options.datasets, { id: startingDatasetId })
			: null

		let selectorOptions =
			datasetSelectorOptions.forButton({ dataset: startingDataset })
		let type = this.dataSourceTypeDropdown.val()
		if (type === 'calculated') {
			selectorOptions.message = i18n.get('datasets.dataset.starting.none')
		}

		this.datasetSelector = new ObjectSelectorButton(selectorOptions)

		this.datasetSelector.on('change', selected => {
			this.onChangeStartingDatasetId()
		})

		// https://github.com/SpiderStrategies/Scoreboard/issues/45540#issuecomment-1050099534
		let showDataset
		if (this.dataSourceTypeDropdown.val() === 'basic') {
			// if there's a starting dataset, we'll just be showing basic
			// fields related to that dataset rather than allowing the user to
			// select any dataset
			showDataset = !this.useStartingDataset
		} else {
			showDataset = false
		}
		this.$('.starting-dataset').toggleClass(
			'is-not-displayed', !showDataset)

		this.$('.starting-dataset .clear').toggleClass('is-not-displayed',
			this.dataSourceTypeDropdown.val() === 'basic')
		this.$('.dataset-selector-button-container').append(this.datasetSelector.el)
		this.datasetSelector.render()
	},

	onClickStartingDatasetClear: function() {
		this.datasetSelector.domainObject = null
		this.datasetSelector.renderLabel(null)
		this.setAggregationsStartingDatasetId(null)
		this.trigger('dataset-selected', null)
		this.renderDataSourceLink()
	},

	renderFieldDropdown: function() {
		let container = this.$('.field')
		const startingDatasetId = _.get(this.datasetSelector.val(), 'id')
		// #35195 Don't show the field dropdown if there is no dataset selected
		const noDatasetSelected = this.showDataset && _.isNil(startingDatasetId)
		if (!this.model || noDatasetSelected ||
			this.dataSourceTypeDropdown.val() === 'calculated' || this.invalidDataset) {
			// Can't render field dropdown until a starting dataset has been chosen
			// or if it's a calculated source
			container.addClass('is-not-displayed')
			return
		}

		container.removeClass('is-not-displayed')

		const dataSource = this.options.dataSource
		const el = this.$('[name=fieldIdentifier]')

		const extraOpts = this._renderFieldDropdownOptions ?
			this._renderFieldDropdownOptions() : {}

		this.fieldField = renderFieldDropdown(_.extend({
			el,
			dataset: this.model,
			allowedFieldIds: this._getAllowedFieldIds(),
			allowedVisibleLinkedFieldIds: this._getAllowedVlfIds(),
			selectedId: this.model.idsToFieldIdentifier(dataSource),
			includeTotal: this.includeTotal,
			includeNativeTotal: this.includeNativeTotal,
			// #58292
			// Allow the `Record Edited` and `Record is Editable` fields to be
			// selected in the field dropdown
			includeEditFields: this.includeEditFields,
			required: true
		}, extraOpts))
	},

	renderDataTypeSelector: function() {
		const containerEl = this.$('.data-type-selector-container')
		if (this.isChartDataSource) containerEl.addClass('is-chart-data-source')

		const initialValue = _.get(this.options, 'dataSource.dataType', 'number')
		const excludedDataTypes = _.get(this.options, 'excludedDataTypes', [])
		this.dataTypeSelector = new DataTypeSelector({
			excludedSuperTypes: 'geographic',
			excludedDataTypes: [ROLLUP, DATASET_LINK].concat(excludedDataTypes),
			initialValue
		})
		this.dataTypeSelector.on('change:dataType', () => this.onChangeDataTypeSelector())
		containerEl.append(this.dataTypeSelector.el)
		this.dataTypeSelector.render()
	},

	renderDataSourceTypeDropdown: function() {
		if (!this.allowCalculated) {
			// still rendering the dataSourceTypeDropdown b/c the rest of this
			// view expects it to be there
			this.$('.type').addClass('is-not-displayed')
		}
		this.dataSourceTypeDropdown = this.$('[name=dataSourceType]')
		let options = [{
			id: 'basic',
			label: i18n.get('common.basic'),
			icon: 'generic'
		},{
			id: 'calculated',
			label: i18n.get('scorecards.node.metric.updateType.calculated'),
			icon: 'calculated'
		}]
		let dataSourceType =
			_.result(this.options.dataSource, 'dataSourceType') || 'basic'
		dropdown.idLabels(this.dataSourceTypeDropdown.empty(), options, dataSourceType)

		this.dataSourceTypeDropdown.on('change', () => {
			this.onChangeDataSourceType(this.dataSourceTypeDropdown.val())
		})
	},

	renderDataSourceLink: function() {
		this.dataSourceLink && this.dataSourceLink.remove()
		let startingDatasetId = _.get(this.datasetSelector.val(), 'id')
		if (!startingDatasetId) {
			return
		}
		this.dataSourceLink = new DataSourceLink({
			section: 'datasets',
			id: startingDatasetId
		})
		this.$('.source-data-link-container').append(this.dataSourceLink.el)
		this.dataSourceLink.render()
	},

	renderEquationButton: function() {
		this.setEquationButton = new EditableButton({
			el: this.$('.set-equation'),
			label: this.equation,
			extraClasses: 'set-equation',
			message: 'scorecards.node.metric.equation.set'
		}).render()
	},

	onChangeDataSourceType: function(type) {
		this.renderFieldDropdown()
		this.$('.equation').add(this.dataTypeSelector.el)
			.toggleClass('is-not-displayed',
			type === 'basic')
		this.$('.data-super-type-container')
			.toggleClass('is-not-displayed', this.numericSumOnly)
		this.formView?.clearErrors()
		this.fieldField && this.fieldField.clearError()
		this.toggleAggregationType()
		this.renderStartingDatasetButton()
		this.renderAggregationType()
		this.renderDataSourceLink()
		// both of these lines need to be after the aggregation type is
		// potentially changed to "use equation" above
		this.updateLabelTypeText()
		this.toggleBasePercentagesOn()
		this.allLinkedFields = this.filterBadges.allLinkedFields =
			this.dataSourceTypeDropdown.val() === 'calculated'
		this.toggleFilters()
		this.toggleBasePercentagesOn()
		this.toggleDecimalPrecision()
	},

	onChangeStartingDatasetId: function() {
		this.toggleFilters()

		let startingDatasetId = _.get(this.datasetSelector.val(), 'id')

		this.setAggregationsStartingDatasetId(startingDatasetId)

		this.renderDataSourceLink()

		// toggle the warning banner if the new starting dataset is different from
		// the old one
		this.toggleWarningBannerIfNeeded(startingDatasetId)

		if (!startingDatasetId) return

		let url = `api/datasets/${startingDatasetId}?fields=true&linkedDatasets=true`

		this._toggleDatasourceLoading(true)

		this.xhr = $.get(url, dataset => {
			this._toggleDatasourceLoading(false)
			this.model = new Dataset(dataset)
			this.filters = []
			this.renderFieldDropdown(dataset)
			this.renderFilterBadges()
			this.trigger('dataset-selected', this.model)
		})
	},

	/**
	 * Sets the startingDatasetId for each aggregation in this.aggregations
	 */
	setAggregationsStartingDatasetId: function (startingDatasetId) {
		_.forEach(this.aggregations, agg => {
			agg.startingDatasetId = startingDatasetId
		})
	},

	_toggleDatasourceLoading: function(loading) {
		this.placeholderSelect.toggle(loading)
		this.$('label.field .marked-field').toggleClass('is-not-displayed', loading)
		// #40560 disable the done button while the data source information is loading
		_.invoke(this.formView.getPrimaryButton(), loading ? 'loading' : 'done')
	},

	/**
	 * Populates the warning banner message with options.warningBannerMessage if
	 * it is set (#58695).  The initial display of the banner is hidden and will
	 * only be shown if a different dataset is selected from the original.
	 */
	renderWarningBanner: function() {
		if (this.options.warningBannerMessage) {
			this.$('.banner-warning').html(this.options.warningBannerMessage)
		}
	},

	/**
	 * Toggles the display of the warning banner based on whether the dataset
	 * selected is the same as the original starting dataset (#58695)
	 * @param datasetId
	 */
	toggleWarningBannerIfNeeded: function (datasetId) {
		// if there is no warning message in the options, don't toggle
		// the warning banner (it is hidden by default)
		if (!this.options.warningBannerMessage) return
		let originalDatasetId = this.getOriginalStartingDatasetId()
		// toggle the warning banner to show if the dataset selected is different
		// from the original starting dataset
		this.$('.banner-warning').toggleClass('is-not-displayed', datasetId == originalDatasetId)
	},

	/**
	 * Returns the original starting dataset id from the options or the model
	 * (pulled out from renderStartingDatasetButton for reuse)
	 */
	getOriginalStartingDatasetId: function() {
		let startingDatasetId = this.options.dataSource.startingDatasetId
		if (!startingDatasetId) {
			// if there was no startingDatasetId saved to the dataSource DatasetAggregation,
			// then try to use the dataset id in the aggregation itself, and if that doesn't
			// have a datasetId, then it is for sure the current dataset
			startingDatasetId = this.options.dataSource.datasetId ||
				_.invoke(this.model, 'get', 'rootDatasetId')
		}
		return startingDatasetId
	},

	/**
	 * Shows or hides the filters panel based on whether a starting dataset
	 * was selected. It doesn't make sense to define filters unless a starting
	 * dataset is defined.
	 */
	toggleFilters: function() {
		this.$('.filters.panel').toggleClass('is-not-displayed',
			!this.filtersAllowed())
	},

	/**
	 * @returns {boolean} whether filters are allowed. It doesn't make sense to
	 * define filters unless a starting dataset is defined.
	 */
	filtersAllowed: function() {
		// #47034 if we're not using a starting dataset, then we can't add filters
		// because the data source's equation can refer to multiple non-linked datasets
		const dataSourceType = this.dataSourceTypeDropdown.val()
		if (dataSourceType == 'calculated' && !this.useStartingDataset) return false

		const startingDatasetId = this.getStartingDatasetId()
		return !_.isNil(startingDatasetId) && !this.invalidDataset
	},

	onChangeFieldIdentifier: function() {
		this.renderAggregationType()
		this.toggleAggregationType()

		const aggTypeSelect = this.aggregationTypeDropdown
		if (!aggTypeSelect) return

		let aggregationType = aggTypeSelect.val()

		let fieldIdentifier = this.getSelectedFieldIdentifier()
		if (fieldIdentifier) {
			let field = this.model.getFieldByFieldIdentifier(fieldIdentifier)
			if (field) {
				// fields with the none aggregation type always use none for the
				// aggregation type
				if (field.aggregationType === 'none') {
					aggregationType = 'none'
				}
			}
			this.updateLabelTypeText({aggregationType})
		}
		this.toggleBasePercentagesOn()
	},

	onChangeDataTypeSelector: function () {
		this.toggleDecimalPrecision()
	},

	/**
	 * Toggles the display of "Decimal Precision"
	 */
	toggleDecimalPrecision: function () {
		let dataSourceType = this.dataSourceTypeDropdown.val()
		let dataType = this.dataTypeSelector.getDataType()
		let displayDecimalPrecision = isDecimalPrecisionDisplayed(dataSourceType, dataType)
		this.$('.precision').toggleClass('is-not-displayed', !displayDecimalPrecision)
	},

	onClickSetEquation: function() {
		let dataSourceAggregationType = _.result(this.aggregationTypeDropdown, 'val')
			// we removed the aggregation type dropdown recently because we
			// couldn't think of a situation where anything other than "use
			// equation" made sense
			|| 'use-equation'
		let startingDatasetId = this.getStartingDatasetId()
		// #45464 #45540 we got rid of the notion of starting dataset in most
		// situations, but it's still needed in some situations. For example,
		// when adding a column to a dataset table
		if (!this.useStartingDataset) {
			startingDatasetId = null
		}
		const datasets = _.extend({}, this.options.datasets, this.datasetsById)
		let form = new DataSourceFieldEquationForm({
			model: this.model,
			datasets,
			startingDatasetId,
			dataType: this.dataTypeSelector.getDataType(),
			data: {
				equation: this.equation,
				aggregations: this.aggregations
			},
			allLinkedFields: this.allLinkedFields,
			dataSourceAggregationType,
			hideAggregationTypeForSameDataset: this.isHideAggregationTypeForSameDataset(),
			individualRecords: this.options.individualRecords,
			treeItemsById: this.treeItemsById
		})
		appDisplay.dialog(form)
		form.on('done', data => {
			this.equation = data.equation
			this.aggregations = data.aggregations
			this.treeItemsById = data.treeItemsById
			// #44654 if there was no starting dataset, then the dataset may
			// have been selected in the form, and returned by it, so add it to
			// our cache of datasetsById
			let newDatasets = _(data.datasetsById).toPairs()
				.map(([id, dataset]) => {
					return [ id, wrapModel(dataset, Dataset) ]
				})
				.fromPairs().value()
			this.datasetsById = _.extend({}, this.datasetsById, newDatasets)

			// #35801 if there is a startingDatasetId, that should also be set
			// on all the aggregations
			// #45699 but only if we're actually using a startingDataset
			if (this.options.useStartingDataset) {
				this.setAggregationsStartingDatasetId(this.getStartingDatasetId())
			} else {
			// if we're not using the starting dataset, don't send a startingDatasetId when
			// validating the equation that the user just set
				this.datasetSelector.val(null)
			}

			this.formView.clearErrors()
			this.formView.change()
			this.setEquationButton.renderLabel(data.equation)
			this.updateLabelTypeText()
		})
	},

	isHideAggregationTypeForSameDataset: function() {
		return this.hideAggregationTypeForSameDataset
	},

	onChangeAggregationType: function() {
		let aggregation
		if (this.aggregationTypeDropdown) {
			aggregation = { aggregationType: this.aggregationTypeDropdown.val() }
		}
		this.updateLabelTypeText(aggregation)
		this.toggleBasePercentagesOn()
	},

	toggleAggregationType: function() {
		let hideAggregationType = !_.result(this, 'showAggregationType')
		let dataSourceType = this.dataSourceTypeDropdown.val()
		// we can't show the aggregation type dropdown until we have a field
		// selected
		let fieldIdentifier = this.getSelectedFieldIdentifier()
		if (dataSourceType === 'basic' && _.isEmpty(fieldIdentifier) ||
			// #28128 default aggregation type for calculated data sources is
			// always "use equation" so we don't need to display the dropdown
			dataSourceType === 'calculated' && !this.options.edit) {
				hideAggregationType = true
		}
		this.$('.aggregation-type').toggleClass(
			'is-not-displayed', hideAggregationType)
	},

	/**
	 * Returns true if the only option for the aggregation type should be "use
	 * equation"
	 */
	isOnlyUseEquationAllowed: function() {
		let type = this.dataSourceTypeDropdown.val()

		if (!this.model) {
			// If there's no starting dataset, we don't have anything to go off
			// of, so only "use equation" is allowed
			return true
		}

		if (type === 'calculated') {
			if (this.options.table) {
				// for tables, we can do things other than use equation for
				// rollup fields only
				let field = this.model.getUnderlyingField(this.options.table.get('fieldId'))
				if (!isGeographicOrRollup(field.dataType)) {
					return true
				}
			} else {
				// if it's not a table it's a single value, in which case only
				// "use equation" is allowed
				return true
			}
		}
		return false
	},

	/**
	 * Populates aggregation types appropriate for the selected field
	 */
	renderAggregationType: function() {
		if (!_.result(this, 'showAggregationType')) return

		this.aggregationTypeDropdown = this.$('[name=aggregationType]')

		let aggregationType =
			this.aggregationTypeDropdown.val() ||
			_.get(this.options, 'dataSource.aggregationType')
		let type = this.dataSourceTypeDropdown.val()
		var fieldIdentifier = this.getSelectedFieldIdentifier()
		// if the user manually cleared out the field ID, don't worry about
		// re-rendering the aggregation type
		if (_.isNil(fieldIdentifier) && type === 'basic') {
			return
		}

		let field, underlyingField
		if (this.model) { // won't be defined b4 a dataset is selected
			field = this.model.getFieldByFieldIdentifier(fieldIdentifier)
			underlyingField = this.model.getUnderlyingField(field)
			fieldIdentifier = _.get(underlyingField, 'id')
		}

		let dataType, options
		// add the "Use Equation" option if it's a calculated column
		if (type === 'calculated') {
			dataType = this.dataTypeSelector.getDataType()
			options = [ aggregationTypeOptions.useEquation ]
			if (!this.isOnlyUseEquationAllowed()) {
				options = options.concat(
					aggregationTypeOptions.nonTotalColumn(dataType))
				// #28296 unique doesn't seem to work
				_.remove(options, option => option.id === 'unique')
			}
		} else {
			dataType = isTotal(fieldIdentifier) ? null : underlyingField.dataType
			options = aggregationTypeOptions.forField(this.model, fieldIdentifier)
		}

		// #14089 use default aggregation type
		if (!_.isNil(fieldIdentifier) && fieldIdentifier != '' && !isTotal(fieldIdentifier) && !aggregationType) {
			aggregationType = this.model.combinedFieldsById[fieldIdentifier].aggregationType
		}
		// if we still don't have an aggregation type, try to use the
		// aggregation type that was previously specified
		if (!aggregationType) {
			aggregationType = this.aggregationTypeDropdown.val()
		}

		// make sure we don't select an invalid aggregation type
		if (_.includes([DATE, TIME], dataType) &&
			!_.includes(['minimum', 'maximum', 'unique',
				LIST_UNIQUE_VALUES], aggregationType)) {
			aggregationType = this.options.defaultDateAggregationType || 'minimum'
		}
		if (dataType == DATE_TIME &&
			!_.includes(['minimum', 'maximum', 'min-date', 'max-date',
				'min-time', 'max-time', 'unique',
				LIST_UNIQUE_VALUES], aggregationType)) {
			aggregationType = this.options.defaultDateAggregationType || 'minimum'
		}
		if (type !== 'calculated' && isTotal(fieldIdentifier) &&
			aggregationType != 'sum' && aggregationType != 'percentage' &&
			aggregationType != 'use-equation') {
			aggregationType = 'sum'
		}

		let availableAggTypes = _.map(options, 'id')
		if (!_.includes(availableAggTypes, aggregationType)) {
			aggregationType = availableAggTypes[0]
		}

		// fields with the none aggregation type always use none for the
		// aggregation type
		let none = _.get(field, 'aggregationType') === 'none'
		if (none) {
			aggregationType = 'none'
		}
		// #18512 #19003 fields with aggregation type = none shouldn't be editable
		this.aggregationTypeDropdown.prop('disabled', none)

		if (!_.get(this, 'allowPercentageAggregationType', true)) {
			_.remove(options, option => option.id === 'percentage')
		}

		if (isDateTimeDataType(dataType) && (
				this.options.parentDomainType === 'chart' ||
				this.options.restrictTemporalToNumUnique)) {
			// #45562 the y-axis value has to be a numeric value, and we can
			// only aggregate date/time fields to a numeric value with
			// "number of unique" (similar to text fields)
			options = [ aggregationTypeOptions.unique ]
			aggregationType = 'unique'
		}

		// #59627 remove the "list unique" aggregation type
		if (!this.allowListUniqueValues) {
			options = _.filter(options, option =>
				option.id !== LIST_UNIQUE_VALUES)
		}

		dropdown.idLabels(this.aggregationTypeDropdown.empty(),
			options, aggregationType)
	},

	renderFilterBadges: function() {
		this.filterBadges = new DatasetsFilterBadges({
			model: this.model,
			filters: this.filters,
			treeItemsById: this.treeItemsById,
			noneMessage: i18n.get('common.thereAreNo', 'report.updateMetricsReport.filters'),
			parentDomainType: this.options.parentDomainType,
			allowAppCalendar: this.options.allowAppCalendar,
			showAggregationFilters: true
		})
		this.filterBadges.on('add-filter edit-filter delete-filter',
			this.formView?.change?.bind(this.formView))
		this.$('.filters .panel-content').html(
			this.filterBadges.el)
		this.filterBadges.render()
		this.$('.filters h3').text(i18n.get('report.updateMetricsReport.filters'))
		this.toggleFilters()
	},

	renderLabelTypeAndLabel: function() {
		selects(this.$('[name=labelType]'))
		this.labelField = new MarkedField({
			el: this.$('[name=label]'),
			validator: requiredValidator
		}).render()
	},

	setInvalidDataset: function(invalid) {
		const oldValue = this.invalidDataset
		this.invalidDataset = invalid
		if (oldValue != invalid) {
			this.renderFieldDropdown()
			this.toggleFilters()
		}
	},

	/**
	 * Toggles the display of the "Base Percentages On" panel
	 */
	toggleBasePercentagesOn: function() {
		let calculated = this.dataSourceTypeDropdown.val() === 'calculated'

		let aggregationType = _.invoke(this.aggregationTypeDropdown, 'val')
		let percentage = aggregationType === 'percentage'

		let showBasePercentagesOn = percentage && !calculated
		this.$('.base-percentages-on').toggleClass(
			'is-not-displayed', !showBasePercentagesOn)
	},

	_getAllowedFieldIds: function() {
		// add total for native dataset
		let options = [ totalOption(this.model) ]
		/* Matt Sgarlata 9/21/21: I don't think this is right. Only VLFs for
		the total fields should be allowed since this dropdown is for basic
		data sources
		// add totals for linked datasets
		_.each(this.model.get('linkedDatasets'), linkedDataset => {
			options.push(totalOption(linkedDataset))
		})
		*/

		// add other non-total fields
		let combinedFields = _.clone(this.model.combinedFields)
		if (this.numericSumOnly) {
			_.remove(combinedFields, option => {
				let underlyingField = this.model.getUnderlyingField(option.id)
				return !isNumericDataType(underlyingField.dataType)
			})
		}
		options.push(... combinedFields)

		return _.map(options, 'id')
	},

	_getAllowedVlfIds: function() {
		let allowedVlfs = this.model.get('visibleLinkedFields')
		if (this.numericSumOnly) {
			allowedVlfs = _.filter(allowedVlfs, vlf => {
				if (isTotal(vlf.fieldId)) return true
				let underlyingField = this.model.getUnderlyingField(vlf.fieldId)
				return isNumericDataType(underlyingField.dataType)
			})
		}
		return _.map(allowedVlfs, 'id')
	},

	addFilter: function() {
		this.filterBadges.addFilter()
	},

	updateLabelTypeText: function(aggregation) {
		if (!this.showLabel) {
			// Don't bother trying to figure out the label type text when we
			// aren't even showing the input
			return ''
		}

		let dataSourceType = this.dataSourceTypeDropdown.val()
		let labelTypeText
		if (dataSourceType === 'calculated') {
			labelTypeText = _.isEmpty(this.equation) ?
				i18n.get('scorecards.node.metric.equation.placeholder') :
				this.equation
		} else {
			// we don't want fields like labelType passed to aggregationLabel
			let fakeAggregation = _.pick(formHelper.readFromDom(this.el), 'fieldIdentifier', 'aggregationType')
			_.extend(fakeAggregation, this.model.fieldIdentifierToIds(fakeAggregation.fieldIdentifier))
			// if the first argument wasn't an event
			if (!_.get(aggregation, 'target')) {
				// use any attributes passed in over those in the DOM (e.g. when
				// the aggregation type is None, it can't be read from the DOM)
				_.extend(fakeAggregation, aggregation)
			}
			labelTypeText = aggregationLabel(this.model, fakeAggregation)
		}

		this.$('[name=labelType] option').eq(0).text(labelTypeText)
		// re-render the select so that it's as wide as it needs to be
		selects(this.$('[name=labelType]'))
	},

	/**
	 * Handles changes to the labelType.  Changes the visibility of the label
	 * input based on the label type select, and hides any errors on the label
	 * input
	 */
	changeLabelType: function() {
		let labelEnabled = this.$('[name=labelType]').val() === 'custom'
		let labelField = this.$('[name=label]').data('view')
		labelField.clearError()
		this.$('.custom-name')
			.toggleClass('is-custom-name', labelEnabled)
	},

	test: function() {
		let xhrComplete = !this.xhr || this.xhr.readyState == XMLHttpRequest.DONE
		if (!xhrComplete) return

		// #19724 verify that fields with none aggregation type are only added
		// to tables they are compatible with
		let { fieldIdentifier } = formHelper.readFromDom(this.el)
		if (fieldIdentifier && xhrComplete) {
			let field = unwrapModel(this.model.getFieldByFieldIdentifier(
				fieldIdentifier, this.model.id))
			if (field && field.aggregationType === 'none') {
				let tableFieldIdentifier = this.model.idsToFieldIdentifier(this.options.table)
				let tableField = this.model.getFieldByFieldIdentifier(tableFieldIdentifier)
				let underlyingTableField = unwrapModel(this.model.getUnderlyingField(tableField.id))
				if (isGeographicOrRollup(underlyingTableField.dataType)) {
					let fieldDataset = this.model.datasetsById[field.datasetId]
					let fieldDatasetPk = unwrapModel(fieldDataset.getUnderlyingField(fieldDataset.get('primaryKeyFieldId')))
					if (fieldDatasetPk.treeId &&
						fieldDatasetPk.treeId != underlyingTableField.treeId) {
						return i18n.get('datasets.choose.columns.incompatibleTrees')
					}
				} else {
					return i18n.get('datasets.choose.columns.noneRequiresTree')
				}
			}
		}

		// make sure calculated columns have an equation
		if (this.dataSourceTypeDropdown.val() === 'calculated' &&
			_.isEmpty(this.equation)) {
			return i18n.get('common.equation.required')
		}

		// validate startingDatasetId is populated for basic data sources
		if (this.dataSourceTypeDropdown.val() === 'basic' && this.showDataset &&
			!this.getStartingDatasetId()) {
			return i18n.get('common.error.required', 'datasets.dataset')
		}

		return this.testFiltersValid()
	},

	/**
	 * Tests that the filters match up with the starting dataset.
	 * @returns {String} an appropriate error message to display to the user,
	 * if applicable
	 */
	testFiltersValid: function() {
		if (!this.filtersAllowed()) return null

		let validDatasetIds = [ this.model.get('rootDatasetId') ]
			.concat(this.model.get('onlyUniquePaths'))
		let hasBadFilter = _.find(this.filters, filter => {
			if (!_.isEmpty(filter.path)) return false // #43421

			let idsToCheck = [ {
				fieldId: filter.fieldId,
				visibleLinkedFieldId: filter.visibleLinkedFieldId
			}, {
				fieldId: filter.anotherTreeFieldId,
				visibleLinkedFieldId: filter.anotherTreeVisibleLinkedFieldId
			}]
			return _.find(idsToCheck, ({ fieldId, visibleLinkedFieldId}) => {
				// no reference to a field is fine
				if (_.isNil(fieldId)) {
					return false
				}
				// #58243 edit fields are fine for filters
				if (isEditField(fieldId)) {
					return false
				}
				let field = this.model.combinedFieldsById[fieldId]
				// if the field isn't found that means we're in an unrelated
				// dataset which isn't OK
				if (!field) {
					return true
				}
				// if there's a VLF we don't need to worry about unique paths
				// b/c the VLF determines the path (#36424)
				if (!_.isNil(visibleLinkedFieldId)) {
					return false
				}
				// otherwise, see if there's a unique path to the field in
				// question
				return !_.includes(validDatasetIds, field.datasetId)
			})
		})
		return hasBadFilter ?
			i18n.get('datasets.dataset.starting.unmatchedFilters') : null
	},

	/**
	 * Reads all data source info from the DOM
	 */
	readFromDom() {
		let dataSource = formHelper.readFromDom(this.el)
		// read the data type for the dataTypeSelector. #57445 this also applies
		// for charts, even though only numeric data types are valid
		dataSource.dataType = this.dataTypeSelector.getDataType()

		delete dataSource.dataSuperType
		if (this.model) {
			_.extend(dataSource, this.model.fieldIdentifierToIds(dataSource.fieldIdentifier))
		}
		dataSource.startingDatasetId = this.getStartingDatasetId()
		let field = this.model
			? this.model.getFieldByFieldIdentifier(dataSource.fieldIdentifier, this.model.id)
			: null

		if (_.get(field, 'aggregationType') === 'none') {
			dataSource.aggregationType = 'none'
		}
		if (this.isOnlyUseEquationAllowed()) {
			dataSource.aggregationType = 'use-equation'
		}
		if (this.numericSumOnly) {
			dataSource.aggregationType = 'sum'
		}
		dataSource.id = this.options.dataSource.id
		if (this.filtersAllowed()) {
			dataSource.filters = this.filters
		} else {
			dataSource.filters = []
		}
		dataSource.denominatorFilters = _.get(this.basePercentagesOnPanel, 'denominatorFilterBadges.filters')
		if (dataSource.dataSourceType === 'calculated') {
			dataSource.equation = this.equation
			dataSource.aggregations = this.aggregations
			delete dataSource.startingDatasetId
		} else {
			delete dataSource.equation
			delete dataSource.aggregations
			dataSource.startingDatasetId = this.getStartingDatasetId() // #31821
		}
		if (field) {
			dataSource.dataType = field.dataType
		}

		if (!dataSource.dataSourceType) {
			dataSource.dataSourceType = 'basic'
		}
		delete dataSource.fieldIdentifier
		return dataSource
	},

	submit: function() {
		let dataSource = _.extend({}, this.options.dataSource, this.readFromDom())
		let done = () => {
			this.trigger('done', dataSource, this.model)
		}

		// we may have loaded a dataset from the API, so if the user closes
		// and opens this form back up we need to have it in this.datasetsById again
		this.options.datasets = _.values(this.datasetsById)

		if (dataSource.dataSourceType === 'basic') {
			delete dataSource.equation
			delete dataSource.aggregations
			done()
		} else {
			let startingDatasetId = this.getStartingDatasetId()
			const url = 'api/datasets/validate-field-equation?' +
				$.param({ startingDatasetId })
			const request = {
				field: dataSource
			}
			return $.post({
				url,
				data: JSON.stringify(request),
				success: done,
				error: xhr => DatasetsEquationForm.onInvalidEquation(
					xhr, this.formView
				)
			})
		}
	},

	getStartingDatasetId() {
		let startingDatasetId = _.get(this.datasetSelector.val(), 'id')
		return startingDatasetId
	},

	getSelectedFieldIdentifier() {
		return this.$('[name=fieldIdentifier]').val()
	},

	dataSourceType() {
		return this.dataSourceTypeDropdown.val()
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.filterBadges, this.labelField,
			this.fieldField, this.basePercentagesOnPanel,
			this.dataSourceTypeDropdown, this.dataTypeSelector,
			this.dataSourceLink], 'remove')
	}

})

/**
 * Returns true if decimal precision should be displayed for the given
 * data source type (Basic or Calculated) and dataType (#48386)
 * @param dataSourceType
 * @param dataType
 * @returns {boolean}
 */
function isDecimalPrecisionDisplayed(dataSourceType, dataType) {
	// decimal precision should always be displayed for basic
	if (dataSourceType === 'basic') {
		return true
	}
	return isNumericDataType(dataType)
}

_.extend(DatasetDataSourceForm, {
	isDecimalPrecisionDisplayed
})
export default DatasetDataSourceForm
export { isDecimalPrecisionDisplayed }

DatasetDataSourceForm.__filename__ = 'datasets/dataset-data-source-form.js'
